import { APP_CHAIN } from '@/client/confs/app';
import { WalletContext } from '@/client/hooks/use-wallet';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAccount, useWalletClient, useDisconnect, useSwitchChain, useConnect } from 'wagmi';

export const WalletProvider = ({ children }: { children: ReactNode }) => {
  const { address, isConnected, isConnecting, chainId } = useAccount();
  const {
    data: walletClient,
    isFetching: walletClientLoading,
    error: walletClientError,
    refetch: retryWalletClient,
  } = useWalletClient();
  const { disconnectAsync: disconnect } = useDisconnect();
  const { openConnectModal, connectModalOpen } = useConnectModal();
  const { switchChainAsync } = useSwitchChain();
  const { connectAsync, connectors, isPending: connectPending } = useConnect();
  const switchRequest = useRef<Promise<unknown>>();
  const switchToAppChain = useCallback(() => {
    // The automatic switch and a sign-in click share the same wallet request.
    if (!switchRequest.current) {
      const request = switchChainAsync({ chainId: APP_CHAIN.id });
      switchRequest.current = request;
      void request
        .finally(() => {
          if (switchRequest.current === request) switchRequest.current = undefined;
        })
        .catch(() => {});
    }
    return switchRequest.current;
  }, [switchChainAsync]);
  const connectInjected = useCallback(async () => {
    const injected = connectors.find(c => c.type === 'injected');
    if (injected) return connectAsync({ connector: injected });
    openConnectModal?.();
  }, [connectors, connectAsync, openConnectModal]);
  const [authSettled, setAuthSettled] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setAuthSettled(true), 300);
    return () => clearTimeout(timer);
  }, []);
  const wrongNetwork = isConnected && chainId !== undefined && chainId !== APP_CHAIN.id;
  useEffect(() => {
    if (wrongNetwork) void switchToAppChain().catch(() => {});
  }, [wrongNetwork, switchToAppChain]);
  useEffect(() => {
    if (isConnected && chainId === APP_CHAIN.id && !walletClient && !walletClientLoading) {
      void retryWalletClient();
    }
    // Retry once per chain landing, not on every failed RPC result.
  }, [isConnected, chainId]);
  const value = useMemo(
    () => ({
      chain: APP_CHAIN,
      ready: authSettled,
      authSettled,
      rawIsConnected: isConnected,
      isConnected: isConnected && !wrongNetwork && !!walletClient,
      isConnecting: isConnecting || connectPending,
      walletClientLoading,
      walletClientError,
      retryWalletClient,
      connectModalOpen: !!connectModalOpen,
      wallet: address ? { address } : undefined,
      walletClient: walletClient ?? undefined,
      disconnect,
      openConnectModal,
      connectInjected,
      wrongNetwork,
      switchToAppChain,
    }),
    [
      authSettled,
      isConnected,
      isConnecting,
      connectPending,
      walletClientLoading,
      walletClientError,
      retryWalletClient,
      connectModalOpen,
      address,
      walletClient,
      disconnect,
      openConnectModal,
      connectInjected,
      wrongNetwork,
      switchToAppChain,
    ]
  );
  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};
